import type { PaymentCorridor } from "@/shared/services/payments/paymentCorridors.service";

export interface CorridorCatalogEntry {
  /** Null when the last read failed: unknown, which every availability predicate reads as allowed. */
  corridors: PaymentCorridor[] | null;
  fetchedAt: number;
  loading: boolean;
}

export interface CorridorCatalogStore {
  get: (key: string) => CorridorCatalogEntry | undefined;
  /** For a screen showing the catalog of `key`; the listener hears about every change. */
  subscribe: (key: string, listener: () => void) => () => void;
  isStale: (key: string) => boolean;
  /** Reads the catalog of `key`, or joins the read of it already on its way. */
  refresh: (key: string, organizationId?: string) => Promise<void>;
  /**
   * Catalogs on screen go stale, keeping their last answer until the new one arrives, and screens are told to
   * read again; the ones nobody shows are dropped.
   */
  invalidate: () => void;
}

/**
 * The corridor catalogs of the session, one per organization, shared by every screen that gates on one. Kept
 * apart from the hook so the caching rules are testable without a DOM.
 */
export function createCorridorCatalogStore(
  fetchCorridors: (organizationId?: string) => Promise<PaymentCorridor[]>,
  staleAfterMs: number,
  now: () => number = Date.now,
): CorridorCatalogStore {
  const entries = new Map<string, CorridorCatalogEntry>();
  const inflight = new Map<string, Promise<void>>();
  const listeners = new Set<() => void>();
  const watchers = new Map<string, number>();

  const notify = () => {
    for (const listener of listeners) listener();
  };

  const refresh = (key: string, organizationId?: string): Promise<void> => {
    const pending = inflight.get(key);
    if (pending) return pending;
    const previous = entries.get(key);
    entries.set(key, {
      corridors: previous?.corridors ?? null,
      fetchedAt: previous?.fetchedAt ?? 0,
      loading: true,
    });
    notify();
    const request: Promise<void> = fetchCorridors(organizationId)
      .then(
        (corridors): PaymentCorridor[] | null => corridors,
        // Unknown, never empty: an empty catalog would read as every rail switched off.
        () => null,
      )
      .then((corridors) => {
        // Invalidated while on its way: the read that replaced it decides.
        if (inflight.get(key) !== request) return;
        inflight.delete(key);
        entries.set(key, { corridors, fetchedAt: now(), loading: false });
        notify();
      });
    inflight.set(key, request);
    return request;
  };

  return {
    get: (key) => entries.get(key),
    subscribe: (key, listener) => {
      listeners.add(listener);
      watchers.set(key, (watchers.get(key) ?? 0) + 1);
      return () => {
        listeners.delete(listener);
        const left = (watchers.get(key) ?? 1) - 1;
        if (left > 0) watchers.set(key, left);
        else watchers.delete(key);
      };
    },
    isStale: (key) => {
      const entry = entries.get(key);
      return !entry || now() - entry.fetchedAt >= staleAfterMs;
    },
    refresh,
    invalidate: () => {
      for (const [key, entry] of entries) {
        if (watchers.has(key)) entries.set(key, { ...entry, fetchedAt: 0, loading: false });
        else entries.delete(key);
      }
      inflight.clear();
      notify();
    },
  };
}
